<template>
  <div class="order-finance-report">
    <!-- Шапка -->
    <div class="ofr-header">
      <div class="ofr-header__title">
        <h1>Финансовые показатели заказов</h1>
      </div>
      <!-- Excel временно скрыт (функционал сохранён) -->
      <div class="ofr-header__actions" v-show="showHeaderActions">
        <b-button
          variant="light"
          class="ofr-btn-outline"
          size="sm"
          :disabled="loading || exporting || totalRows === 0"
          @click="exportExcel"
        >
          <b-spinner v-if="exporting" small class="mr-1"></b-spinner>
          <b-icon v-else icon="file-earmark-spreadsheet" class="mr-1"></b-icon>
          Excel
        </b-button>
      </div>
    </div>

    <!-- Фильтры -->
    <div class="ofr-card ofr-filters" :class="{ 'ofr-filters--open': filtersOpen }">
      <div class="ofr-filters__header">
        <button
          type="button"
          class="ofr-filters__toggle"
          :aria-expanded="filtersOpen ? 'true' : 'false'"
          aria-controls="ofr-filters-body"
          @click="toggleFilters"
        >
          <b-icon icon="funnel" class="ofr-filters__toggle-icon"></b-icon>
          <span class="ofr-filters__toggle-title">Фильтры</span>
          <span v-if="activeFiltersCount > 0" class="ofr-filters__count">{{ activeFiltersCount }}</span>
          <span v-if="appliedFilters" class="ofr-filters__summary">
            Период: {{ formatDate(appliedFilters.date_from) }} - {{ formatDate(appliedFilters.date_to) }}
            · по дате {{ dateTypeGenitive(appliedFilters.date_type) }}
          </span>
        </button>

        <button
          id="ofr-columns-btn"
          type="button"
          class="ofr-filters__columns"
          :class="{ 'ofr-filters__columns--hidden': hiddenColumns.length > 0 }"
          :title="columnsButtonTitle"
        >
          <b-icon icon="layout-three-columns"></b-icon>
          <span class="ofr-filters__columns-text">Колонки</span>
          <span v-if="hiddenColumns.length > 0" class="ofr-filters__columns-badge">
            <b-icon icon="eye-slash" class="mr-1"></b-icon>{{ hiddenColumns.length }}
          </span>
        </button>
        <b-popover target="ofr-columns-btn" triggers="click blur" placement="bottomleft" custom-class="ofr-columns-popover">
          <template #title>Конструктор колонок</template>
          <tsoft-context-menu-headers
            header-context-menu-name="orderFinanceReport"
            menu-name="orderFinanceReportColumns"
            :menu-items="columns"
          />
          <div class="ofr-columns-popover__footer">
            <span v-if="hiddenColumns.length > 0" class="ofr-columns-popover__hidden">
              Скрыто: {{ hiddenColumns.length }}
            </span>
            <b-link v-if="hiddenColumns.length > 0" @click="showAllColumns">Показать все</b-link>
            <span v-else class="ofr-columns-popover__all">Показаны все колонки</span>
          </div>
        </b-popover>

        <button
          type="button"
          class="ofr-filters__chevron"
          :aria-expanded="filtersOpen ? 'true' : 'false'"
          aria-controls="ofr-filters-body"
          :title="filtersOpen ? 'Свернуть' : 'Развернуть'"
          @click="toggleFilters"
        >
          <b-icon :icon="filtersOpen ? 'chevron-up' : 'chevron-down'"></b-icon>
        </button>
      </div>

      <b-collapse id="ofr-filters-body" :visible="filtersOpen">
        <div class="ofr-filters__body">
          <div class="ofr-presets">
            <span class="ofr-label mr-2">Быстрый период:</span>
            <button
              v-for="preset in periodPresets"
              :key="preset.key"
              type="button"
              class="ofr-chip"
              :class="{ 'ofr-chip--active': activePreset === preset.key }"
              @click="applyPreset(preset.key)"
            >{{ preset.text }}</button>
          </div>

          <b-row>
            <b-col cols="12" md="6" xl="3" class="mb-3">
              <label class="ofr-label">
                Период
                <b-icon
                  v-b-tooltip.hover.html="{ title: hints.period, customClass: 'ofr-tooltip' }"
                  icon="info-circle"
                  class="ofr-label__hint"
                ></b-icon>
              </label>
              <div class="ofr-period">
                <b-form-input v-model="filters.date_from" type="date" size="sm" :max="filters.date_to" @input="activePreset = null" />
                <span class="ofr-period__dash">-</span>
                <b-form-input v-model="filters.date_to" type="date" size="sm" :min="filters.date_from" @input="activePreset = null" />
              </div>
            </b-col>
            <b-col cols="12" md="6" xl="5" class="mb-3">
              <label class="ofr-label">
                Фильтр периода по дате
                <b-icon
                  v-b-tooltip.hover.html="{ title: hints.dateType, customClass: 'ofr-tooltip' }"
                  icon="info-circle"
                  class="ofr-label__hint"
                ></b-icon>
              </label>
              <b-form-radio-group
                v-model="filters.date_type"
                :options="options.date_types"
                buttons
                size="sm"
                button-variant="light"
                class="ofr-segmented"
              />
            </b-col>
            <b-col cols="12" md="6" xl="4" class="mb-3">
              <label class="ofr-label">
                Клиент
                <b-icon
                  v-b-tooltip.hover.html="{ title: hints.companies, customClass: 'ofr-tooltip' }"
                  icon="info-circle"
                  class="ofr-label__hint"
                ></b-icon>
              </label>
              <multiselect
                v-model="filters.companies"
                :options="companyOptions"
                :multiple="true"
                :options-limit="300"
                :close-on-select="false"
                :limit="2"
                :limit-text="limitText"
                label="name"
                track-by="id"
                placeholder="Все клиенты"
                select-label=""
                deselect-label=""
                selected-label=""
              >
                <span slot="noResult">Ничего не найдено</span>
              </multiselect>
            </b-col>
            <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
              <label class="ofr-label">
                Услуга
                <b-icon
                  v-b-tooltip.hover.html="{ title: hints.services, customClass: 'ofr-tooltip' }"
                  icon="info-circle"
                  class="ofr-label__hint"
                ></b-icon>
              </label>
              <multiselect
                v-model="filters.services"
                :options="options.services"
                :multiple="true"
                :searchable="false"
                :close-on-select="false"
                :limit="2"
                :limit-text="limitText"
                label="text"
                track-by="value"
                placeholder="Все услуги"
                select-label=""
                deselect-label=""
                selected-label=""
              />
            </b-col>
            <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
              <label class="ofr-label">
                КАМ
                <b-icon
                  v-b-tooltip.hover.html="{ title: hints.kams, customClass: 'ofr-tooltip' }"
                  icon="info-circle"
                  class="ofr-label__hint"
                ></b-icon>
              </label>
              <multiselect
                v-model="filters.kams"
                :options="kamOptions"
                :multiple="true"
                :close-on-select="false"
                :limit="2"
                :limit-text="limitText"
                label="name"
                track-by="id"
                placeholder="Все КАМы"
                select-label=""
                deselect-label=""
                selected-label=""
              >
                <span slot="noResult">Ничего не найдено</span>
              </multiselect>
            </b-col>
            <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
              <label class="ofr-label">
                Тип клиента
                <b-icon
                  v-b-tooltip.hover.html="{ title: hints.clientTypes, customClass: 'ofr-tooltip' }"
                  icon="info-circle"
                  class="ofr-label__hint"
                ></b-icon>
              </label>
              <multiselect
                v-model="filters.client_types"
                :options="options.client_types"
                :multiple="true"
                :searchable="false"
                label="text"
                track-by="value"
                placeholder="Все типы"
                select-label=""
                deselect-label=""
                selected-label=""
              />
            </b-col>
            <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
              <label class="ofr-label">
                Статус заказа
                <b-icon
                  v-b-tooltip.hover.html="{ title: hints.statuses, customClass: 'ofr-tooltip' }"
                  icon="info-circle"
                  class="ofr-label__hint"
                ></b-icon>
              </label>
              <multiselect
                v-model="filters.statuses"
                :options="options.statuses"
                :multiple="true"
                :searchable="false"
                label="text"
                track-by="value"
                placeholder="Все статусы"
                select-label=""
                deselect-label=""
                selected-label=""
              />
            </b-col>
            <b-col cols="12" class="ofr-filters__buttons">
              <b-button variant="light" class="ofr-btn-outline mr-2" size="sm" :disabled="loading" @click="resetFilters">
                Сбросить
              </b-button>
              <b-button class="ofr-btn-primary" size="sm" :disabled="loading || !isPeriodValid" @click="applyFilters">
                <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                Применить
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- KPI (итоги за период) - скрываются вместе с фильтром -->
        <div class="ofr-filters__kpis">
          <div class="ofr-kpis">
            <div class="ofr-kpi" v-for="kpi in kpis" :key="kpi.key">
              <div class="ofr-kpi__icon" :class="'ofr-kpi__icon--' + kpi.color">
                <b-icon :icon="kpi.icon"></b-icon>
              </div>
              <div class="ofr-kpi__body">
                <div class="ofr-kpi__label">
                  {{ kpi.label }}
                  <b-icon
                    v-b-tooltip.hover.html="{ title: kpi.info, customClass: 'ofr-tooltip' }"
                    icon="info-circle"
                    class="ofr-label__hint"
                  ></b-icon>
                </div>
                <div class="ofr-kpi__value" :class="kpi.valueClass">
                  <b-skeleton v-if="loading" width="70%"></b-skeleton>
                  <template v-else>{{ kpi.value }}</template>
                </div>
                <div class="ofr-kpi__hint">{{ kpi.hint }}</div>
              </div>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>

    <b-alert :show="!!errorMessage" variant="danger" dismissible @dismissed="errorMessage = ''">
      {{ errorMessage }}
    </b-alert>

    <!-- Таблица -->
    <div class="ofr-card ofr-table-card">
      <b-table
        :items="displayRows"
        :fields="visibleFields"
        :busy="loading || tableLoading"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :tbody-tr-class="rowClass"
        no-local-sorting
        primary-key="row_key"
        sticky-header="70vh"
        responsive
        small
        hover
        show-empty
        no-sort-reset
        class="ofr-table mb-0"
        :class="{ 'ofr-table--id-sticky': isOrderIdVisible }"
        @sort-changed="onSortChanged"
      >
        <template #head()="data">
          <span class="ofr-th">
            {{ data.label }}
            <b-icon
              v-if="data.field.hint"
              v-b-tooltip.hover.html="{ title: data.field.hint, customClass: 'ofr-tooltip' }"
              icon="info-circle"
              class="ofr-th__hint"
            ></b-icon>
          </span>
        </template>

        <template #table-busy>
          <div class="ofr-state">
            <b-spinner class="mr-2"></b-spinner>
            {{ loading ? 'Формируем отчёт…' : 'Загружаем…' }}
          </div>
        </template>

        <template #empty>
          <div class="ofr-state">
            <b-icon icon="inbox" font-scale="2" class="mb-2"></b-icon>
            <div>Нет заказов за выбранный период</div>
          </div>
        </template>

        <!--
          Под раскрытым заказом идут строки его услуг (item.is_service) в той же таблице:
          деньги - в тех же колонках, название услуги - в первой видимой из колонок
          Клиент / Услуга / ID (serviceLabelField), остальные ячейки пустые.
        -->
        <template #cell(order_id)="{ item }">
          <span v-if="item.is_service && serviceLabelField === 'order_id'" class="ofr-service-label">
            {{ item.service_name }}
          </span>
          <template v-else-if="!item.is_service">
            <button
              v-if="expandField === 'order_id' && canExpand(item)"
              type="button"
              class="ofr-expand"
              :class="{ 'ofr-expand--open': isExpanded(item) }"
              :title="isExpanded(item) ? 'Скрыть услуги заказа' : 'Показать, из чего складывается сумма по услугам'"
              @click="toggleExpanded(item)"
            >
              <b-icon :icon="isExpanded(item) ? 'dash' : 'plus'"></b-icon>
            </button>
            <span v-else-if="expandField === 'order_id'" class="ofr-expand-spacer"></span>
            <router-link :to="'/orders/' + item.order_id" target="_blank" class="ofr-link">{{ item.order_id }}</router-link>
          </template>
        </template>

        <template #cell(company_name)="{ item }">
          <span v-if="item.is_service && serviceLabelField === 'company_name'" class="ofr-service-label">
            {{ item.service_name }}
            <span v-if="item.excluded" class="ofr-service-tag">не в фильтре</span>
          </span>
          <template v-else-if="!item.is_service">
            <button
              v-if="expandField === 'company_name' && canExpand(item)"
              type="button"
              class="ofr-expand"
              :class="{ 'ofr-expand--open': isExpanded(item) }"
              :title="isExpanded(item) ? 'Скрыть услуги заказа' : 'Показать, из чего складывается сумма по услугам'"
              @click="toggleExpanded(item)"
            >
              <b-icon :icon="isExpanded(item) ? 'dash' : 'plus'"></b-icon>
            </button>
            <span v-else-if="expandField === 'company_name'" class="ofr-expand-spacer"></span>
            <span class="ofr-client">{{ item.company_name }}</span>
          </template>
        </template>

        <template #cell(kam)="{ item }">
          <template v-if="!item.is_service">
            <span v-if="item.kam">{{ item.kam }}</span>
            <span v-else class="text-muted">-</span>
          </template>
        </template>

        <template #cell(services)="{ item }">
          <span v-if="!item.is_service">{{ item.services }}</span>
          <span v-else-if="serviceLabelField === 'services'" class="ofr-service-label">{{ item.service_name }}</span>
        </template>

        <template #cell(client_type_label)="{ item }">
          <span v-if="!item.is_service" class="ofr-badge" :class="'ofr-badge--client-' + item.client_type">{{ item.client_type_label }}</span>
        </template>

        <template #cell(status_label)="{ item }">
          <span v-if="!item.is_service" class="ofr-badge" :class="'ofr-badge--status-' + item.status">{{ item.status_label }}</span>
        </template>

        <template #cell(created_date)="{ item, value }">{{ item.is_service ? '' : formatDate(value) }}</template>
        <template #cell(loading_date)="{ item, value }">{{ item.is_service ? '' : formatDate(value) }}</template>
        <template #cell(unloading_date)="{ item, value }">{{ item.is_service ? '' : formatDate(value) }}</template>
        <template #cell(paid_date)="{ item, value }">{{ item.is_service ? '' : formatDate(value) }}</template>
        <template #cell(closed_date)="{ item, value }">{{ item.is_service ? '' : formatDate(value) }}</template>

        <template #cell(revenue)="{ item, value }">
          <span :class="{ 'ofr-zero': item.is_service && value === 0 }">{{ formatMoney(value) }}</span>
        </template>
        <template #cell(costs)="{ item, value }">
          <span :class="{ 'ofr-zero': item.is_service && value === 0 }">{{ formatMoney(value) }}</span>
        </template>
        <template #cell(gp)="{ item, value }">
          <span :class="{ 'ofr-negative': value < 0, 'ofr-zero': item.is_service && value === 0 }">{{ formatMoney(value) }}</span>
        </template>
        <template #cell(margin)="{ value }">
          <span class="ofr-margin" :class="marginClass(value)">{{ formatPercent(value) }}</span>
        </template>

        <template #custom-foot>
          <b-tr class="ofr-total-row">
            <b-td
              v-for="field in visibleFields"
              :key="'total_' + field.key"
              :class="field.tdClass"
              :sticky-column="!!field.stickyColumn"
            >
              <template v-if="field.key === 'order_id'">
                Итого:<br>
                {{ totals.orders }} <span class="ofr-total-sub">{{ plural(totals.orders, 'заказ', 'заказа', 'заказов') }}</span>
              </template>
              <template v-else-if="field.key === 'company_name'">
                {{ totals.clients }} <span class="ofr-total-sub">{{ plural(totals.clients, 'клиент', 'клиента', 'клиентов') }}</span>
              </template>
              <template v-else-if="field.key === 'revenue'">{{ formatMoney(totals.revenue) }}</template>
              <template v-else-if="field.key === 'costs'">{{ formatMoney(totals.costs) }}</template>
              <template v-else-if="field.key === 'gp'">
                <span :class="{ 'ofr-negative': totals.gp < 0 }">{{ formatMoney(totals.gp) }}</span>
              </template>
              <template v-else-if="field.key === 'margin'">
                <span class="ofr-margin" :class="marginClass(totals.margin)">{{ formatPercent(totals.margin) }}</span>
              </template>
            </b-td>
          </b-tr>
        </template>
      </b-table>

      <div class="ofr-pagination" v-if="totalRows > 0">
        <div class="ofr-pagination__info">
          Показано {{ shownFrom }}–{{ shownTo }} из {{ totalRows }}
        </div>
        <b-pagination
          v-if="perPage > 0 && totalRows > perPage"
          :value="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          :disabled="tableLoading"
          size="sm"
          limit="5"
          class="mb-0 ofr-pager"
          @change="onPageChange"
        />
        <div class="ofr-pagination__size">
          <span class="ofr-label mr-2 mb-0">Строк:</span>
          <b-form-select v-model="perPage" :options="perPageOptions" size="sm" :disabled="tableLoading" @change="onPerPageChange" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import {
  API_ORDER_FINANCE_REPORT_DATA,
  API_ORDER_FINANCE_REPORT_FILTER_DATA,
  API_POST_GENERATE_EXCEL
} from '@/constants/api.js'

const TABLE_SETTINGS_NAME = 'orderFinanceReport'

// Применённые фильтры, быстрый период, сортировка и размер страницы - в userLocalSettings
// (filterHeaderSettings → localStorage), чтобы не сбрасывались при повторном входе на страницу
const FILTERS_SETTINGS_NAME = 'orderFinanceReport'

const PERIOD_PRESETS = ['month', 'prev_month', 'quarter', 'year']

// Как считаются деньги - общее для выручки, затрат и итогов
const MONEY_RULES = 'Без НДС. Берутся выставленные суммы (а не оплаченные); черновики и удалённые счета не учитываются. ' +
  'При фильтре по услугам - только счета выбранных услуг.'

// Статусы отчёта - этапы из ТЗ, а не статусы карточки заказа
const STATUS_HINT = 'Этап заказа для отчёта (это не статус из карточки заказа):<br>' +
  '<b>Закрыт</b> - заказ в статусе «Закрыт»;<br>' +
  '<b>Выгружен</b> - есть дата выгрузки / выпуска ДТ или статус «Завершен» / «Ожидает оплаты»;<br>' +
  '<b>В пути</b> - есть дата погрузки / регистрации ДТ;<br>' +
  '<b>Новый</b> - всё остальное.<br>' +
  'Заказы в статусах «Отказ» и «Невозможно взыскать» в отчёт не входят.'

// Колонки и их расшифровка: что означает поле и из чего складывается цифра
const COLUMNS = [
  { key: 'order_id', label: 'ID Номер заказа', hint: 'ID номер заказа в Танасофт. Ссылка открывает карточку заказа в новой вкладке.', stickyColumn: true, tdClass: 'ofr-col-id', thClass: 'ofr-col-id' },
  { key: 'company_name', label: 'Клиент', hint: 'Название клиента из карточки заказа.', stickyColumn: true, tdClass: 'ofr-col-client', thClass: 'ofr-col-client' },
  { key: 'kam', label: 'КАМ', hint: 'Ответственный / куратор клиента по услугам заказа (из карточки компании):<br>• ТО, Сертификация, Консалтинг, ПНК, СВХ - менеджер по ведению ВЭД;<br>• ТЭО, Страхование - менеджер ТЭО.<br>Если в заказе обе группы услуг - оба менеджера.' },
  { key: 'services', label: 'Услуга', hint: 'Все услуги заказа списком. Один заказ - одна строка.' },
  { key: 'client_type_label', label: 'Тип клиента', hint: '<b>Новый</b> - первые 6 месяцев с даты первого заказа клиента, дальше - <b>Действующий</b>.<br>Дата заказа - дата погрузки (если в заказе только ТО - дата регистрации ДТ), а если её нет - дата создания заказа.' },
  { key: 'created_date', label: 'Дата создания заказа', hint: 'Дата создания заказа в Танасофт.', tdClass: 'ofr-col-date' },
  { key: 'status_label', label: 'Статус заказа', hint: STATUS_HINT },
  { key: 'loading_date', label: 'Дата погрузки/регистрации декларации', hint: 'Дата принятия груза в работу.<br>Если в заказе есть ТЭО - фактическая дата погрузки из услуги ТЭО (самая ранняя).<br>Если только ТО - дата регистрации ДТ (из номера декларации).', tdClass: 'ofr-col-date' },
  { key: 'unloading_date', label: 'Дата выгрузки/дата выпуска', hint: 'Операционное закрытие (фиксация услуги) - день фактической передачи груза получателю.<br>Если в заказе есть ТЭО - фактическая дата доставки из услуги ТЭО (самая поздняя).<br>Если только ТО - дата выпуска ДТ.', tdClass: 'ofr-col-date' },
  { key: 'paid_date', label: 'Дата оплаты', hint: 'Дата, когда клиент полностью оплатил все счета по заказу (дата последнего платежа).<br>Пусто - есть неоплаченные или частично оплаченные счета клиенту либо счетов клиенту нет.', tdClass: 'ofr-col-date' },
  { key: 'closed_date', label: 'Дата закрытия сделки', hint: 'Сделка закрыта: есть операционное и финансовое закрытие (фиксация маржи).<br>Дата последнего перевода заказа в статус «Закрыт». Пусто - заказ ещё не закрыт.', tdClass: 'ofr-col-date' },
  { key: 'revenue', label: 'Выручка (revenue), руб', hint: 'Сумма, которую платит клиент за комплекс услуг: все счета клиенту по заказу.<br>' + MONEY_RULES, tdClass: 'ofr-col-num', thClass: 'text-right' },
  { key: 'costs', label: 'Затраты, руб', hint: 'Сумма всех затрат по сделке: все счета от подрядчиков (линии, порты, брокеры и т. д.) по заказу.<br>' + MONEY_RULES, tdClass: 'ofr-col-num', thClass: 'text-right' },
  { key: 'gp', label: 'GP, руб', hint: 'GP = Выручка - Затраты.', tdClass: 'ofr-col-num', thClass: 'text-right' },
  { key: 'margin', label: 'Маржинальность по заказу %', hint: 'Маржинальность = (Выручка - Затраты) / Выручка × 100%.<br>В итогах - сумма GP / сумма выручки за период (средневзвешенная, а не среднее по заказам).<br>Пусто - у заказа нет выручки.', tdClass: 'ofr-col-num', thClass: 'text-right' }
]

// Пояснения к полям фильтра
const HINTS = {
  period: 'Период, за который заказы попадают в отчёт (обе даты включительно).<br>С какой датой заказа сравнивается период - выбирается в «Фильтре периода по дате».',
  dateType: 'По какой дате заказ попадает в период (этапы по движению заказа):<br>' +
    '<b>Создание</b> - дата создания заказа;<br>' +
    '<b>Загрузка</b> - дата погрузки (если в заказе только ТО - регистрации ДТ);<br>' +
    '<b>Выгрузка</b> - дата доставки (если только ТО - выпуска ДТ);<br>' +
    '<b>Оплата</b> - клиент полностью оплатил все счета;<br>' +
    '<b>Закрытие</b> - перевод заказа в статус «Закрыт».<br>' +
    'Если нужной даты у заказа нет, в отчёт он не попадает.',
  companies: 'Клиент из карточки заказа.<br>В списке только клиенты, у которых есть заказы в отчёте при остальных фильтрах. Список обновляется после «Применить».',
  kams: 'Куратор клиента по услугам заказа: ТО-услуги - менеджер по ведению ВЭД, ТЭО и Страхование - менеджер ТЭО.<br>В списке только КАМ, у которых есть заказы в отчёте при остальных фильтрах. Список обновляется после «Применить».',
  clientTypes: '<b>Новый</b> - первые 6 месяцев с даты первого заказа клиента, дальше - <b>Действующий</b>.<br>Дата заказа - дата погрузки (если в заказе только ТО - дата регистрации ДТ), а если её нет - дата создания заказа.',
  statuses: STATUS_HINT,
  services: 'Показывает заказы с выбранными услугами, а выручку, затраты, GP и маржинальность считает <b>только по счетам этих услуг</b> - так видно, сколько денег приносит, например, только ТЭО.<br>' +
    'Услуга счёта - услуга заказа, к которой привязан счёт. Если счёт не привязан, а услуга в заказе одна, деньги относятся к ней; ' +
    'иначе такие деньги не попадают ни в одну услугу (видны только без фильтра по услугам).'
}

const EXCEL_FORMATS = {
  order_id: '0',
  revenue: '#,##0.00',
  costs: '#,##0.00',
  gp: '#,##0.00',
  margin: '0.00%'
}

const DATE_TYPE_GENITIVE = {
  created: 'создания',
  loading: 'загрузки',
  unloading: 'выгрузки',
  payment: 'оплаты',
  closed: 'закрытия'
}

const moneyFormatter = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const moneyShortFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 })
const percentFormatter = new Intl.NumberFormat('ru-RU', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 })

// Деньги счетов, которые нельзя отнести к одной услуге (SERVICE_UNALLOCATED на сервере)
const SERVICE_UNALLOCATED = 0

function roundMoney(value) {
  return Math.round(value * 100) / 100
}

function emptyTotals() {
  return { clients: 0, orders: 0, revenue: 0, costs: 0, gp: 0, margin: null }
}

export default {
  name: 'OrderFinanceReport',
  data() {
    return {
      // loading - пересчёт отчёта (фильтры), tableLoading - сортировка/страница по кэшу на сервере
      loading: false,
      tableLoading: false,
      requestId: 0,
      exporting: false,
      errorMessage: '',
      options: {
        // Клиенты и КАМ приходят вместе с данными отчёта - только те, что есть в выдаче
        companies: [],
        services: [],
        kams: [],
        statuses: [],
        client_types: [],
        // Порядок - по движению заказа
        date_types: [
          { value: 'created', text: 'Создание' },
          { value: 'loading', text: 'Загрузка' },
          { value: 'unloading', text: 'Выгрузка' },
          { value: 'payment', text: 'Оплата' },
          { value: 'closed', text: 'Закрытие' }
        ]
      },
      hints: HINTS,
      filters: this.defaultFilters(),
      appliedFilters: null,
      // Снимок применённых фильтров и быстрого периода - то, что сохраняется в localStorage
      appliedState: null,
      activePreset: 'year',
      showHeaderActions: false,
      // При входе на страницу фильтр (вместе с итогами) открыт
      filtersOpen: true,
      periodPresets: [
        { key: 'month', text: 'Текущий месяц' },
        { key: 'prev_month', text: 'Прошлый месяц' },
        { key: 'quarter', text: 'Квартал' },
        { key: 'year', text: 'Год' }
      ],
      columns: COLUMNS.map(column => Object.assign({ visible: true, sortable: true }, column)),
      rows: [],
      // Заказы, у которых раскрыты строки услуг: { order_id: true }
      expandedOrders: {},
      totalRows: 0,
      totals: emptyTotals(),
      sortBy: '',
      sortDesc: false,
      perPage: 50,
      perPageOptions: [
        { value: 50, text: '50' },
        { value: 100, text: '100' },
        { value: 200, text: '200' },
        { value: 0, text: 'Все' }
      ],
      currentPage: 1
    }
  },
  computed: {
    activeFiltersCount() {
      return ['services', 'companies', 'kams', 'client_types', 'statuses']
        .filter(key => this.filters[key].length > 0)
        .length
    },
    // Клиенты / КАМ из выдачи + уже выбранные (чтобы выбор не пропадал, если их нет в новом периоде)
    companyOptions() {
      return this.withSelected(this.options.companies, this.filters.companies)
    },
    kamOptions() {
      return this.withSelected(this.options.kams, this.filters.kams)
    },
    hiddenColumns() {
      return this.columns.filter(column => !column.visible)
    },
    // Подсказка на кнопке: какие колонки сейчас скрыты (чтобы не забыть про них)
    columnsButtonTitle() {
      if (this.hiddenColumns.length === 0) {
        return 'Конструктор колонок'
      }
      return 'Скрыто колонок: ' + this.hiddenColumns.length + ' — ' +
        this.hiddenColumns.map(column => column.label).join(', ')
    },
    isOrderIdVisible() {
      return this.visibleFields.some(field => field.key === 'order_id')
    },
    visibleFields() {
      return this.columns.filter(column => column.visible)
    },
    isPeriodValid() {
      return !!this.filters.date_from && !!this.filters.date_to && this.filters.date_from <= this.filters.date_to
    },
    shownFrom() {
      if (this.perPage === 0) return 1
      return (this.currentPage - 1) * this.perPage + 1
    },
    shownTo() {
      if (this.perPage === 0) return this.totalRows
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
    // Строки таблицы: заказ, а под раскрытым заказом - строки его услуг с теми же колонками денег
    displayRows() {
      const result = []
      this.rows.forEach(row => {
        result.push(row)
        if (this.expandedOrders[row.order_id] && this.canExpand(row)) {
          this.serviceBreakdown(row).forEach(item => result.push(item))
        }
      })
      return result
    },
    serviceNames() {
      const names = {}
      names[SERVICE_UNALLOCATED] = 'Без услуги'
      this.options.services.forEach(service => {
        names[service.value] = service.text
      })
      return names
    },
    // Где кнопка «+»: в ID, а если ID скрыт - в «Клиенте»
    expandField() {
      if (this.isFieldVisible('order_id')) return 'order_id'
      if (this.isFieldVisible('company_name')) return 'company_name'
      return null
    },
    // Где название услуги в строке услуги: Клиент (закреплён, виден при скролле) → Услуга → ID
    serviceLabelField() {
      return ['company_name', 'services', 'order_id'].find(key => this.isFieldVisible(key)) || null
    },
    isServiceFilterApplied() {
      return !!(this.appliedFilters && this.appliedFilters.service_ids && this.appliedFilters.service_ids.length)
    },
    appliedServiceNames() {
      if (!this.isServiceFilterApplied) return ''
      const names = {}
      this.options.services.forEach(service => {
        names[service.value] = service.text
      })
      return this.appliedFilters.service_ids.map(id => names[id] || id).join(', ')
    },
    kpis() {
      const t = this.totals
      // При фильтре по услугам деньги - только по счетам выбранных услуг
      const scope = this.isServiceFilterApplied ? '<br>Только по услугам: ' + this.appliedServiceNames + '.' : ''
      return [
        { key: 'clients', label: 'Активных клиентов', value: t.clients, hint: 'за период', icon: 'people', color: 'blue',
          info: 'Число разных клиентов среди заказов отчёта (с учётом всех фильтров).' },
        { key: 'orders', label: 'Заказов', value: t.orders, hint: 'за период', icon: 'box-seam', color: 'blue',
          info: 'Число заказов в отчёте - строк таблицы (с учётом всех фильтров).' },
        { key: 'revenue', label: 'Выручка', value: this.formatMoneyShort(t.revenue), hint: 'без НДС', icon: 'cash-stack', color: 'green',
          info: 'Сумма выручки всех заказов отчёта - все счета клиентам.<br>' + MONEY_RULES + scope },
        { key: 'costs', label: 'Затраты', value: this.formatMoneyShort(t.costs), hint: 'без НДС', icon: 'wallet2', color: 'gray',
          info: 'Сумма затрат всех заказов отчёта - все счета от подрядчиков.<br>' + MONEY_RULES + scope },
        { key: 'gp', label: 'GP', value: this.formatMoneyShort(t.gp), hint: 'Выручка − Затраты', icon: 'graph-up', color: 'orange', valueClass: { 'ofr-negative': t.gp < 0 },
          info: 'GP = Выручка - Затраты по всем заказам отчёта.' + scope },
        { key: 'margin', label: 'Маржинальность', value: this.formatPercent(t.margin), hint: 'GP / Выручка', icon: 'percent', color: 'orange', valueClass: this.marginClass(t.margin),
          info: 'Сумма GP / сумма выручки × 100% - средневзвешенная по всем заказам отчёта, а не среднее по заказам.' + scope }
      ]
    }
  },
  async created() {
    this.restoreSavedFilters()
    this.loadFilterData()
    await this.restoreColumnSettings()
    this.applyFilters()
  },
  methods: {
    defaultFilters() {
      return {
        date_from: this.$moment().startOf('year').format('YYYY-MM-DD'),
        date_to: this.$moment().format('YYYY-MM-DD'),
        date_type: 'loading',
        services: [],
        companies: [],
        kams: [],
        client_types: [],
        statuses: []
      }
    },
    loadFilterData() {
      axios.get(API_ORDER_FINANCE_REPORT_FILTER_DATA)
        .then(({ data }) => {
          const result = data.data || {}
          this.options.services = result.services || []
          this.options.statuses = result.statuses || []
          this.options.client_types = result.client_types || []
          if (result.date_types) {
            this.options.date_types = result.date_types
          }
        })
        .catch(() => {
          this.errorMessage = 'Не удалось загрузить данные для фильтров'
        })
    },
    async restoreColumnSettings() {
      try {
        const settings = await this.$store.dispatch('userLocalSettings/GET_TABLE_HEADER_SETTINGS', TABLE_SETTINGS_NAME)
        if (settings) {
          this.columns.forEach(column => {
            if (settings[column.key] && typeof settings[column.key].visible === 'boolean') {
              column.visible = settings[column.key].visible
            }
          })
        }
      } catch (e) {
        // настройки колонок не критичны - показываем все
      }
    },
    /**
     * Восстановить последние применённые фильтры, сортировку и размер страницы.
     * Если был выбран быстрый период («Год», «Текущий месяц»...), даты пересчитываются от сегодня.
     * Повреждённые или устаревшие значения пропускаются - берутся значения по умолчанию.
     */
    restoreSavedFilters() {
      const saved = this.$store.getters['userLocalSettings/FILTER_HEADER_SETTINGS'][FILTERS_SETTINGS_NAME]
      if (!saved || !saved.filters || typeof saved.filters !== 'object') {
        return
      }

      const filters = saved.filters
      const defaults = this.defaultFilters()
      const isDate = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      const listOf = (value, key) => Array.isArray(value)
        ? value.filter(item => item && typeof item === 'object' && item[key] !== undefined && item[key] !== null)
        : []

      this.filters = {
        date_from: isDate(filters.date_from) ? filters.date_from : defaults.date_from,
        date_to: isDate(filters.date_to) ? filters.date_to : defaults.date_to,
        date_type: DATE_TYPE_GENITIVE[filters.date_type] ? filters.date_type : defaults.date_type,
        services: listOf(filters.services, 'value'),
        companies: listOf(filters.companies, 'id'),
        kams: listOf(filters.kams, 'id'),
        client_types: listOf(filters.client_types, 'value'),
        statuses: listOf(filters.statuses, 'value')
      }

      if (PERIOD_PRESETS.includes(saved.preset)) {
        this.applyPreset(saved.preset)
        this.activePreset = saved.preset
      } else {
        this.activePreset = null
      }
      if (!this.isPeriodValid) {
        this.filters.date_from = defaults.date_from
        this.filters.date_to = defaults.date_to
        this.activePreset = 'year'
      }

      if (typeof saved.sort_by === 'string' && (saved.sort_by === '' || this.columns.some(column => column.key === saved.sort_by))) {
        this.sortBy = saved.sort_by
        this.sortDesc = !!saved.sort_desc
      }
      if (this.perPageOptions.some(option => option.value === saved.per_page)) {
        this.perPage = saved.per_page
      }
    },
    saveFilters() {
      if (!this.appliedState) {
        return
      }
      this.$store.commit('userLocalSettings/SET_FILTER_HEADER_SETTINGS', {
        name: FILTERS_SETTINGS_NAME,
        value: {
          filters: this.appliedState.filters,
          preset: this.appliedState.preset,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          per_page: this.perPage
        }
      })
    },
    isFieldVisible(key) {
      return this.visibleFields.some(field => field.key === key)
    },
    /**
     * Услуги заказа для разбивки: все услуги заказа (даже без счетов) + услуги, к которым
     * привязаны счета, + «Без услуги», если есть нераспределённые деньги.
     * Порядок - как в фильтре по услугам, «Без услуги» в конце.
     */
    serviceIdsOf(row) {
      const ids = {}
      ;(row.service_ids || []).forEach(id => {
        ids[id] = true
      })
      Object.keys(row.by_service || {}).forEach(id => {
        ids[Number(id)] = true
      })
      const order = this.options.services.map(service => service.value)
      const rank = id => (id === SERVICE_UNALLOCATED ? 1e6 : (order.indexOf(id) === -1 ? 1e5 + id : order.indexOf(id)))
      return Object.keys(ids).map(Number).sort((a, b) => rank(a) - rank(b))
    },
    // «+» нужен, только если у заказа больше одной услуги
    canExpand(row) {
      return this.serviceIdsOf(row).length > 1
    },
    /**
     * Строки услуг под заказом: деньги по каждой услуге в тех же колонках.
     * Суммы по услугам округляются до копеек, а копеечная разница округления относится
     * к самой крупной услуге - чтобы строки услуг в сумме ровно давали строку заказа.
     * При фильтре по услугам невыбранные услуги видны, но помечены (excluded) и в сумму не входят.
     */
    serviceBreakdown(row) {
      const money = row.by_service || {}
      const selected = this.isServiceFilterApplied ? this.appliedFilters.service_ids : null
      const items = this.serviceIdsOf(row).map(id => {
        const raw = money[id] || { revenue: 0, costs: 0 }
        return {
          row_key: row.row_key + '_s' + id,
          is_service: true,
          order_id: row.order_id,
          service_id: id,
          service_name: this.serviceNames[id] || String(id),
          excluded: !!selected && !selected.includes(id),
          rawRevenue: raw.revenue,
          rawCosts: raw.costs,
          revenue: roundMoney(raw.revenue),
          costs: roundMoney(raw.costs)
        }
      })

      const included = items.filter(item => !item.excluded)
      ;[['revenue', 'rawRevenue'], ['costs', 'rawCosts']].forEach(([key, rawKey]) => {
        const diff = roundMoney(row[key] - included.reduce((sum, item) => sum + item[key], 0))
        if (diff !== 0 && included.length) {
          const largest = included.reduce((max, item) => (Math.abs(item[rawKey]) > Math.abs(max[rawKey]) ? item : max))
          largest[key] = roundMoney(largest[key] + diff)
        }
      })

      items.forEach((item, index) => {
        item.gp = roundMoney(item.revenue - item.costs)
        item.margin = item.revenue !== 0 ? item.gp / item.revenue : null
        item.last = index === items.length - 1
      })
      return items
    },
    isExpanded(row) {
      return !!this.expandedOrders[row.order_id]
    },
    toggleExpanded(row) {
      if (this.expandedOrders[row.order_id]) {
        this.$delete(this.expandedOrders, row.order_id)
      } else {
        this.$set(this.expandedOrders, row.order_id, true)
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return null
      if (item.is_service) {
        return {
          'ofr-row-service': true,
          'ofr-row-service--excluded': item.excluded,
          'ofr-row-service--last': item.last
        }
      }
      return { 'ofr-row--expanded': this.isExpanded(item) && this.canExpand(item) }
    },
    withSelected(options, selected) {
      const ids = {}
      options.forEach(option => {
        ids[option.id] = true
      })
      return selected.filter(option => !ids[option.id]).concat(options)
    },
    toggleFilters() {
      this.filtersOpen = !this.filtersOpen
    },
    showAllColumns() {
      const settings = {}
      this.columns.forEach(column => {
        column.visible = true
        settings[column.key] = { visible: true }
      })
      this.$store.dispatch('userLocalSettings/SET_TABLE_HEADER_SETTINGS_SMART', {
        tableName: TABLE_SETTINGS_NAME,
        settings: settings
      })
    },
    applyPreset(key) {
      const now = this.$moment()
      let from = now.clone()
      let to = now.clone()
      switch (key) {
        case 'month':
          from = now.clone().startOf('month')
          break
        case 'prev_month':
          from = now.clone().subtract(1, 'month').startOf('month')
          to = now.clone().subtract(1, 'month').endOf('month')
          break
        case 'quarter':
          from = now.clone().startOf('quarter')
          break
        case 'year':
          from = now.clone().startOf('year')
          break
      }
      this.filters.date_from = from.format('YYYY-MM-DD')
      this.filters.date_to = to.format('YYYY-MM-DD')
      this.$nextTick(() => {
        this.activePreset = key
      })
    },
    resetFilters() {
      this.filters = this.defaultFilters()
      this.$nextTick(() => {
        this.activePreset = 'year'
        this.applyFilters()
      })
    },
    buildRequest() {
      return {
        date_from: this.filters.date_from,
        date_to: this.filters.date_to,
        date_type: this.filters.date_type,
        service_ids: this.filters.services.map(item => item.value),
        company_ids: this.filters.companies.map(item => item.id),
        kam_ids: this.filters.kams.map(item => item.id),
        client_types: this.filters.client_types.map(item => item.value),
        statuses: this.filters.statuses.map(item => item.value)
      }
    },
    applyFilters() {
      if (!this.isPeriodValid) {
        return
      }
      this.appliedFilters = this.buildRequest()
      // Сохраняем именно применённые фильтры, а не изменённые, но не применённые
      this.appliedState = {
        filters: JSON.parse(JSON.stringify(this.filters)),
        preset: this.activePreset
      }
      this.saveFilters()
      this.expandedOrders = {}
      this.currentPage = 1
      this.loadPage(true)
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy || ''
      this.sortDesc = !!ctx.sortDesc
      this.saveFilters()
      this.currentPage = 1
      this.loadPage(false)
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadPage(false)
    },
    onPerPageChange() {
      this.saveFilters()
      this.currentPage = 1
      this.loadPage(false)
    },
    pageRequest(refresh, page, perPage) {
      return Object.assign({}, this.appliedFilters, {
        sort_by: this.sortBy || null,
        sort_desc: this.sortDesc,
        page: page,
        per_page: perPage,
        refresh: refresh
      })
    },
    /**
     * Сортировка и пагинация - на сервере по всему отчёту.
     * refresh = true пересчитывает отчёт (Применить), иначе сервер берёт его из кэша.
     */
    loadPage(refresh) {
      if (!this.appliedFilters) {
        return
      }
      const requestId = ++this.requestId
      this.loading = refresh
      this.tableLoading = !refresh
      this.errorMessage = ''
      axios.post(API_ORDER_FINANCE_REPORT_DATA, this.pageRequest(refresh, this.currentPage, this.perPage))
        .then(({ data }) => {
          if (requestId !== this.requestId) return
          const result = data.data || {}
          const pagination = result.pagination || {}
          this.rows = (result.rows || []).map(row => Object.assign(row, { row_key: 'o' + row.order_id }))
          this.totals = result.totals || emptyTotals()
          this.options.companies = result.companies || []
          this.options.kams = result.kams || []
          this.totalRows = pagination.total || 0
          this.currentPage = pagination.page || 1
        })
        .catch(error => {
          if (requestId !== this.requestId) return
          this.errorMessage = this.requestErrorMessage(error)
        })
        .finally(() => {
          if (requestId !== this.requestId) return
          this.loading = false
          this.tableLoading = false
        })
    },
    requestErrorMessage(error) {
      const response = error.response || {}
      if (response.status === 403) {
        return 'Недостаточно прав для просмотра отчёта'
      }
      if (response.status === 422) {
        const errors = (response.data && response.data.errors) || {}
        const firstError = Object.keys(errors).length ? errors[Object.keys(errors)[0]][0] : ''
        return 'Проверьте фильтры. ' + firstError
      }
      return 'Не удалось сформировать отчёт. Попробуйте ещё раз'
    },
    exportExcel() {
      if (!this.appliedFilters) {
        return
      }
      // В таблице только текущая страница - для выгрузки берём все строки в текущей сортировке
      this.exporting = true
      axios.post(API_ORDER_FINANCE_REPORT_DATA, this.pageRequest(false, 1, 0))
        .then(({ data }) => this.generateExcel((data.data && data.data.rows) || []))
        .catch(() => {
          this.errorMessage = 'Не удалось сформировать Excel-файл'
          this.exporting = false
        })
    },
    generateExcel(rows) {
      const headers = {}
      this.visibleFields.forEach(field => {
        headers[field.key] = { name: field.label, format: EXCEL_FORMATS[field.key] || '@' }
      })

      const items = rows.map(row => this.excelRow(row))
      const totalRow = {}
      this.visibleFields.forEach(field => {
        totalRow[field.key] = ''
      })
      totalRow.order_id = 'Итого: ' + this.totals.orders
      totalRow.company_name = this.totals.clients
      totalRow.revenue = this.totals.revenue
      totalRow.costs = this.totals.costs
      totalRow.gp = this.totals.gp
      totalRow.margin = this.totals.margin === null ? '' : this.totals.margin
      items.push(this.pickVisible(totalRow))

      const filters = this.appliedFilters || this.buildRequest()
      const dataForExport = {
        sheets: [{
          settings: {
            title: 'Финансовые показатели заказов за период ' + this.formatDate(filters.date_from) + ' - ' +
              this.formatDate(filters.date_to) + ' (по дате ' + this.dateTypeGenitive(filters.date_type) + ')' +
              (this.isServiceFilterApplied ? ', только услуги: ' + this.appliedServiceNames : ''),
            sheet_title: 'Финансовые показатели'
          },
          headers: headers,
          items: items
        }]
      }

      axios.post(API_POST_GENERATE_EXCEL, dataForExport)
        .then(({ data }) => {
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', '')
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch(() => {
          this.errorMessage = 'Не удалось сформировать Excel-файл'
        })
        .finally(() => {
          this.exporting = false
        })
    },
    excelRow(row) {
      return this.pickVisible({
        company_name: row.company_name,
        kam: row.kam,
        services: row.services,
        client_type_label: row.client_type_label,
        order_id: row.order_id,
        created_date: this.formatDate(row.created_date),
        status_label: row.status_label,
        loading_date: this.formatDate(row.loading_date, ''),
        unloading_date: this.formatDate(row.unloading_date, ''),
        paid_date: this.formatDate(row.paid_date, ''),
        closed_date: this.formatDate(row.closed_date, ''),
        revenue: row.revenue,
        costs: row.costs,
        gp: row.gp,
        margin: row.margin === null ? '' : row.margin
      })
    },
    pickVisible(source) {
      const result = {}
      this.visibleFields.forEach(field => {
        result[field.key] = source[field.key]
      })
      return result
    },
    formatDate(value, empty) {
      if (!value) return empty === undefined ? '-' : empty
      return this.$moment(value, 'YYYY-MM-DD').format('DD.MM.YYYY')
    },
    formatMoney(value) {
      return moneyFormatter.format(value || 0)
    },
    formatMoneyShort(value) {
      return moneyShortFormatter.format(value || 0) + ' ₽'
    },
    formatPercent(value) {
      if (value === null || value === undefined) return '-'
      return percentFormatter.format(value)
    },
    marginClass(value) {
      if (value === null || value === undefined) return 'ofr-margin--none'
      if (value < 0) return 'ofr-margin--negative'
      if (value < 0.1) return 'ofr-margin--low'
      return 'ofr-margin--good'
    },
    dateTypeGenitive(type) {
      return DATE_TYPE_GENITIVE[type] || DATE_TYPE_GENITIVE.loading
    },
    limitText(count) {
      return 'и ещё ' + count
    },
    plural(n, one, few, many) {
      const mod10 = n % 10
      const mod100 = n % 100
      if (mod10 === 1 && mod100 !== 11) return one
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
      return many
    }
  }
}
</script>

<style lang="scss">
// Корпоративные цвета (resources/sass/_variables.scss)
$ofr-green: #0D6247;
$ofr-green-hover: #0c4f3a;
$ofr-green-light: #eaf5f0;
$ofr-orange: #EA7B27;
$ofr-orange-hover: #da640a;
$ofr-blue: #2B538A;
$ofr-gray: #5a6268;
$ofr-gray-light: #e4e6eb;
$ofr-red: #e3342f;
$ofr-radius: 10px;
$ofr-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.08);

.order-finance-report {
  padding-bottom: 24px;

  .ofr-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 16px;

    h1 {
      margin-bottom: 4px;
      color: $ofr-green;
    }

    &__actions {
      display: flex;
      margin-top: 8px;

      .btn + .btn {
        margin-left: 8px;
      }
    }
  }

  .ofr-card {
    background: #fff;
    border: 1px solid $ofr-gray-light;
    border-radius: $ofr-radius;
    box-shadow: $ofr-shadow;
    margin-bottom: 16px;
  }

  .ofr-label {
    display: block;
    margin-bottom: 4px;
    color: $ofr-gray;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;

    // Значок ⓘ у подписи поля фильтра / плитки итогов - как у заголовков таблицы
    &__hint {
      margin-left: 2px;
      color: #9aa0a6;
      cursor: help;
      vertical-align: -0.1em;

      &:hover {
        color: $ofr-green;
      }
    }
  }

  // Кнопки
  .ofr-btn-primary {
    background: $ofr-orange;
    border-color: $ofr-orange;
    color: #fff;
    min-width: 120px;

    &:hover,
    &:focus,
    &:not(:disabled):active {
      background: $ofr-orange-hover;
      border-color: $ofr-orange-hover;
      color: #fff;
    }
  }

  .ofr-btn-outline {
    background: #fff;
    border: 1px solid $ofr-gray-light;
    color: $ofr-green;

    &:hover,
    &:focus {
      background: $ofr-green-light;
      border-color: $ofr-green;
      color: $ofr-green;
    }
  }

  // Фильтры (компактные элементы управления)
  // По фактической высоте vue-multiselect (~35px), чтобы все контролы фильтра были одной высоты
  $ofr-control-height: 36px;
  $ofr-control-font: 0.78rem;

  // Без overflow: hidden - иначе выпадающие списки обрезаются границами карточки
  .ofr-filters {
    position: relative;
    z-index: 10;

    &__header {
      display: flex;
      align-items: center;
      padding-right: 8px;
      border-radius: $ofr-radius;
    }

    &__toggle {
      display: flex;
      flex: 1 1 auto;
      align-items: center;
      min-width: 0;
      padding: 10px 8px 10px 16px;
      background: transparent;
      border: 0;
      border-radius: $ofr-radius 0 0 $ofr-radius;
      color: #212529;
      font-size: 0.85rem;
      text-align: left;
      cursor: pointer;

      &:hover {
        background: #fafbfc;
      }

      &:focus {
        outline: none;
        box-shadow: inset 0 0 0 2px rgba($ofr-green, 0.2);
      }
    }

    &__toggle-icon {
      margin-right: 8px;
      color: $ofr-green;
    }

    &__toggle-title {
      font-weight: 600;
    }

    &__count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      margin-left: 6px;
      padding: 0 5px;
      background: $ofr-orange;
      border-radius: 999px;
      color: #fff;
      font-size: 0.7rem;
      font-weight: 700;
    }

    &__summary {
      overflow: hidden;
      margin-left: 12px;
      color: $ofr-gray;
      font-size: 0.78rem;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    // Компактная кнопка «Колонки» и кнопка свернуть/развернуть
    &__columns,
    &__chevron {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      height: 26px;
      background: #fff;
      border: 1px solid $ofr-gray-light;
      border-radius: 5px;
      color: $ofr-gray;
      font-size: 0.74rem;
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        border-color: $ofr-green;
        color: $ofr-green;
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba($ofr-green, 0.2);
      }
    }

    &__columns {
      padding: 0 8px;
      margin-right: 6px;
    }

    // Есть скрытые колонки - кнопка подсвечена, чтобы о них не забыть
    &__columns--hidden {
      background: rgba($ofr-orange, 0.08);
      border-color: $ofr-orange;
      color: $ofr-orange-hover;

      &:hover {
        border-color: $ofr-orange-hover;
        color: $ofr-orange-hover;
      }
    }

    &__columns-badge {
      display: inline-flex;
      align-items: center;
      height: 16px;
      margin-left: 6px;
      padding: 0 5px;
      background: $ofr-orange;
      border-radius: 999px;
      color: #fff;
      font-size: 0.68rem;
      font-weight: 700;
    }

    &__columns-text {
      margin-left: 5px;
    }

    &__chevron {
      width: 26px;
      padding: 0;
    }

    &--open &__header {
      border-bottom: 1px solid $ofr-gray-light;
      border-radius: $ofr-radius $ofr-radius 0 0;
    }

    &__body {
      padding: 10px 16px 6px;

      .ofr-label {
        margin-bottom: 2px;
        font-size: 0.68rem;
      }

      .mb-3 {
        margin-bottom: 10px !important;
      }
    }

    &__buttons {
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      padding-top: 4px;

      .btn {
        height: $ofr-control-height;
        padding-top: 0;
        padding-bottom: 0;
        font-size: $ofr-control-font;
        line-height: $ofr-control-height - 2px;
      }

      .ofr-btn-primary {
        min-width: 100px;
      }
    }
  }

  .ofr-presets {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 8px;

    .ofr-label {
      display: inline-block;
      margin-bottom: 0;
    }
  }

  .ofr-chip {
    margin: 2px 4px 2px 0;
    padding: 1px 10px;
    background: #fff;
    border: 1px solid $ofr-gray-light;
    border-radius: 999px;
    color: $ofr-gray;
    font-size: 0.72rem;
    line-height: 1.5;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: $ofr-green;
      color: $ofr-green;
    }

    &--active,
    &--active:hover {
      background: $ofr-green;
      border-color: $ofr-green;
      color: #fff;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba($ofr-green, 0.25);
    }
  }

  .ofr-period {
    display: flex;
    align-items: center;

    .form-control {
      min-width: 0;
      height: $ofr-control-height;
      padding: 0 6px;
      border-color: #e8e8e8;
      border-radius: 5px;
      font-size: $ofr-control-font;
    }

    &__dash {
      margin: 0 4px;
      color: $ofr-gray;
      font-size: $ofr-control-font;
    }
  }

  .ofr-segmented {
    display: flex;
    width: 100%;

    // Без overflow: hidden - внутри кнопки скрытый radio, при клике браузер «прокручивает»
    // к нему содержимое кнопки, и текст съезжает вниз
    .btn {
      flex: 1 1 0;
      min-width: 0;
      height: $ofr-control-height;
      padding: 0 2px;
      background: #fff;
      border-color: $ofr-gray-light;
      color: $ofr-gray;
      font-size: 0.74rem;
      line-height: $ofr-control-height - 2px;
      white-space: nowrap;

      &.active,
      &:not(:disabled):not(.disabled).active {
        background: $ofr-green;
        border-color: $ofr-green;
        color: #fff;
      }

      &.focus,
      &:focus {
        box-shadow: none;
      }
    }
  }

  // vue-multiselect в компактном размере
  .multiselect {
    min-height: $ofr-control-height;
    font-size: $ofr-control-font;
  }

  .multiselect__tags {
    min-height: $ofr-control-height;
    padding: 3px 28px 0 6px;
    font-size: $ofr-control-font;
  }

  .multiselect__select {
    width: 28px;
    height: $ofr-control-height - 2px;
    padding: 0 6px;

    &:before {
      top: 60%;
    }
  }

  .multiselect__placeholder {
    margin-bottom: 3px;
    padding-top: 1px;
    padding-left: 2px;
    font-size: $ofr-control-font;
  }

  .multiselect__input,
  .multiselect__single {
    min-height: 18px;
    margin-bottom: 3px;
    font-size: $ofr-control-font;
    line-height: 18px;
  }

  .multiselect__strong {
    margin-bottom: 3px;
    font-size: 0.72rem;
    line-height: 18px;
  }

  // Теги выбранных значений. Глобальный form.scss делает текст серым,
  // а крестик крупным и слева поверх текста - возвращаем крестик вправо.
  .multiselect__tag {
    max-width: 100%;
    margin: 0 4px 3px 0;
    padding: 3px 24px 3px 8px;
    background: $ofr-green;
    border-radius: 4px;
    color: #fff;
    font-size: 0.72rem;
    line-height: 14px;
    vertical-align: top;
  }

  .multiselect__tag-icon {
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    width: 20px;
    margin-left: 0;
    border-radius: 0 4px 4px 0;
    line-height: 20px;

    &:after {
      color: rgba(255, 255, 255, 0.8);
      font-size: 14px;
    }

    &:hover {
      background: $ofr-green-hover;
    }

    &:hover:after {
      color: #fff;
    }
  }

  // «и ещё N» при ограничении числа тегов
  .multiselect__strong {
    color: $ofr-gray;
  }

  .multiselect__option {
    min-height: 30px;
    padding: 7px 10px;
    font-size: $ofr-control-font;
    line-height: 16px;
  }

  // Глобально (sass/default/form.scss) у пункта под курсором серый текст - на зелёном не читается
  .multiselect__option--highlight,
  .multiselect__option--highlight:after,
  .multiselect__option--highlight span {
    background: $ofr-green;
    color: #fff;
  }

  .multiselect__option--selected.multiselect__option--highlight,
  .multiselect__option--selected.multiselect__option--highlight:after,
  .multiselect__option--selected.multiselect__option--highlight span {
    background: $ofr-green-hover;
    color: #fff;
  }

  // KPI
  .ofr-filters__kpis {
    padding: 12px 16px 14px;
    background: #f8f9fa;
    border-top: 1px solid $ofr-gray-light;
    border-radius: 0 0 $ofr-radius $ofr-radius;

    .ofr-kpis {
      margin-bottom: 0;
    }

    .ofr-kpi {
      box-shadow: none;
    }
  }

  .ofr-kpis {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    grid-gap: 12px;
    margin-bottom: 16px;
  }

  .ofr-kpi {
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 14px;
    background: #fff;
    border: 1px solid $ofr-gray-light;
    border-radius: $ofr-radius;
    box-shadow: $ofr-shadow;

    &__icon {
      display: flex;
      flex: 0 0 40px;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      margin-right: 12px;
      border-radius: 10px;
      font-size: 1.15rem;

      &--green {
        background: $ofr-green-light;
        color: $ofr-green;
      }

      &--orange {
        background: rgba($ofr-orange, 0.12);
        color: $ofr-orange;
      }

      &--blue {
        background: rgba($ofr-blue, 0.1);
        color: $ofr-blue;
      }

      &--gray {
        background: $ofr-gray-light;
        color: $ofr-gray;
      }
    }

    &__body {
      min-width: 0;
      flex: 1 1 auto;
    }

    &__label {
      color: $ofr-gray;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    &__value {
      overflow: hidden;
      color: #212529;
      font-size: 1.25rem;
      font-weight: 700;
      line-height: 1.3;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-variant-numeric: tabular-nums;
    }

    &__hint {
      color: #9aa0a6;
      font-size: 0.72rem;
    }
  }

  // Таблица
  .ofr-table-card {
    overflow: hidden;
  }

  .ofr-table {
    font-size: 0.82rem;

    &.b-table-sticky-header {
      margin-bottom: 0;
    }

    table {
      margin-bottom: 0;
    }

    thead th {
      background: #f1f3f5 !important;
      border-color: #dee2e6 !important;
      border-bottom: 1px solid #ced4da !important;
      color: #212529;
      font-weight: 600;
      vertical-align: middle;
      white-space: normal;
      min-width: 110px;
    }

    thead th[aria-sort] {
      background-image: none !important;
    }

    thead th[aria-sort="ascending"] .ofr-th:after {
      content: ' ↑';
    }

    thead th[aria-sort="descending"] .ofr-th:after {
      content: ' ↓';
    }

    tbody td {
      vertical-align: middle;
      border-color: $ofr-gray-light;
    }

    tbody tr:nth-of-type(even) td {
      background-color: #fafbfc;
    }

    tbody tr:hover td {
      background-color: $ofr-green-light;
    }

    .b-table-sticky-column {
      background-color: #fff;
    }

    tbody tr:nth-of-type(even) td.b-table-sticky-column {
      background-color: #fafbfc;
    }

    tbody tr:hover td.b-table-sticky-column {
      background-color: $ofr-green-light;
    }

    tfoot td {
      position: sticky;
      bottom: 0;
      z-index: 2;
      background: $ofr-green-light;
      border-top: 2px solid $ofr-green;
      color: $ofr-green;
      font-weight: 700;
      white-space: nowrap;
    }

    // Перебиваем стили bootstrap-vue (.table-b-table-default, z-index: 2),
    // иначе при горизонтальном скролле ячейки итогов наезжают на «Итого:»
    tfoot td.b-table-sticky-column,
    tfoot td.b-table-sticky-column.table-b-table-default {
      z-index: 4 !important;
      background: $ofr-green-light !important;
      color: $ofr-green !important;
      box-shadow: 1px 0 0 $ofr-gray-light;
    }
  }

  .ofr-th {
    display: inline;

    &__hint {
      margin-left: 2px;
      color: $ofr-gray;
      cursor: help;
      vertical-align: -0.1em;
    }
  }

  // Кнопка «+» - раскрыть деньги заказа по услугам
  .ofr-expand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-right: 6px;
    padding: 0;
    background: #fff;
    border: 1px solid $ofr-gray-light;
    border-radius: 4px;
    color: $ofr-green;
    font-size: 0.8rem;
    line-height: 1;
    vertical-align: middle;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: $ofr-green;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba($ofr-green, 0.2);
    }

    &--open {
      background: $ofr-green;
      border-color: $ofr-green;
      color: #fff;
    }
  }

  // Место под «+» у заказов с одной услугой: номера заказов стоят ровно
  .ofr-expand-spacer {
    display: inline-block;
    width: 18px;
    margin-right: 6px;
  }

  // Раскрытый заказ и строки его услуг - одна группа: зелёная полоса слева,
  // заказ на лёгком зелёном фоне, услуги - на своём (фон задан явно, в том числе для
  // закреплённых колонок, чтобы не путаться с «зеброй» основных строк)
  .ofr-table tbody tr.ofr-row--expanded > td,
  .ofr-table tbody tr.ofr-row--expanded:hover > td,
  .ofr-table tbody tr.ofr-row--expanded > td.b-table-sticky-column,
  .ofr-table tbody tr.ofr-row--expanded:hover > td.b-table-sticky-column {
    background-color: #eef6f2;
  }

  .ofr-table tbody tr.ofr-row-service > td,
  .ofr-table tbody tr.ofr-row-service:hover > td,
  .ofr-table tbody tr.ofr-row-service > td.b-table-sticky-column,
  .ofr-table tbody tr.ofr-row-service:hover > td.b-table-sticky-column {
    padding-top: 0.15rem;
    padding-bottom: 0.15rem;
    background-color: #f7faf8;
    border-top-color: #edf2ef;
    color: $ofr-gray;
    font-size: 0.76rem;
  }

  .ofr-table tbody tr.ofr-row--expanded > td:first-child,
  .ofr-table tbody tr.ofr-row-service > td:first-child {
    box-shadow: inset 3px 0 0 $ofr-green;
  }

  .ofr-table tbody tr.ofr-row-service--last > td {
    border-bottom: 1px solid #d6e6dd;
  }

  .ofr-service-label {
    display: inline-block;
    padding-left: 20px;
    color: $ofr-green;
    font-weight: 600;
    white-space: nowrap;

    &:before {
      content: '↳';
      margin-right: 6px;
      color: #9aa0a6;
      font-weight: 400;
    }
  }

  .ofr-service-tag {
    margin-left: 6px;
    padding: 0 6px;
    border: 1px solid $ofr-gray-light;
    border-radius: 999px;
    color: #9aa0a6;
    font-size: 0.66rem;
    font-weight: 600;
  }

  // Нет денег по услуге - ноль приглушён, сразу видно, где деньги есть
  .ofr-zero {
    color: #b8bdc2;
  }

  // Услуга не входит в фильтр по услугам: её деньги не учтены в строке заказа
  .ofr-table tbody tr.ofr-row-service--excluded > td,
  .ofr-table tbody tr.ofr-row-service--excluded > td .ofr-service-label,
  .ofr-table tbody tr.ofr-row-service--excluded > td .ofr-margin,
  .ofr-table tbody tr.ofr-row-service--excluded > td .ofr-negative {
    color: #b8bdc2;
  }

  // Закреплены две колонки: ID (фиксированной ширины) и Клиент сразу за ним
  $ofr-id-col-width: 100px;

  .ofr-table .ofr-col-id {
    width: $ofr-id-col-width;
    min-width: $ofr-id-col-width !important;
    max-width: $ofr-id-col-width;
    white-space: normal;
    font-variant-numeric: tabular-nums;
  }

  .ofr-table--id-sticky .b-table-sticky-column.ofr-col-client {
    left: $ofr-id-col-width !important;
  }

  .ofr-col-client {
    min-width: 180px;
    max-width: 260px;
  }

  .ofr-col-num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .ofr-col-date {
    white-space: nowrap;
  }

  .ofr-client {
    font-weight: 600;
  }

  .ofr-link {
    color: $ofr-blue;
    font-weight: 600;

    &:hover {
      color: $ofr-orange;
    }
  }

  .ofr-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;

    &--status-new {
      background: rgba($ofr-blue, 0.1);
      color: $ofr-blue;
    }

    &--status-in_transit {
      background: rgba($ofr-orange, 0.14);
      color: $ofr-orange-hover;
    }

    &--status-unloaded {
      background: rgba(77, 192, 181, 0.16);
      color: #2a8a80;
    }

    &--status-closed {
      background: $ofr-green;
      color: #fff;
    }

    &--client-new {
      background: #fff;
      border: 1px solid $ofr-orange;
      color: $ofr-orange-hover;
    }

    &--client-existing {
      background: #fff;
      border: 1px solid $ofr-gray-light;
      color: $ofr-gray;
    }
  }

  .ofr-negative {
    color: $ofr-red;
  }

  .ofr-margin {
    font-weight: 700;

    &--good {
      color: $ofr-green;
    }

    &--low {
      color: $ofr-orange-hover;
    }

    &--negative {
      color: $ofr-red;
    }

    &--none {
      color: #9aa0a6;
      font-weight: 400;
    }
  }

  .ofr-total-sub {
    font-weight: 400;
  }

  .ofr-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 16px;
    color: $ofr-gray;

    .spinner-border {
      color: $ofr-green;
      margin-bottom: 8px;
    }
  }

  // Пагинация
  .ofr-pagination {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-top: 1px solid $ofr-gray-light;

    &__info {
      color: $ofr-gray;
      font-size: 0.8rem;
      margin: 4px 0;
    }

    &__size {
      display: flex;
      align-items: center;
      margin: 4px 0;

      .ofr-label {
        display: inline-block;
      }

      .custom-select {
        width: auto;
      }
    }
  }

  .ofr-pager {
    margin: 4px 0;

    .page-item.active .page-link {
      background: $ofr-green;
      border-color: $ofr-green;
      color: #fff;
    }

    .page-link {
      color: $ofr-green;
    }
  }

  @media (max-width: 1399.98px) {
    .ofr-kpis {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 767.98px) {
    .ofr-header h1 {
      font-size: 1.4rem;
    }

    // На телефоне у «Колонки» остаётся только иконка
    .ofr-filters__columns {
      width: 26px;
      padding: 0;
    }

    .ofr-filters__columns-text {
      display: none;
    }

    // С бейджем скрытых колонок кнопка расширяется
    .ofr-filters__columns--hidden {
      width: auto;
      padding: 0 6px;
    }

    .ofr-kpis {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-gap: 8px;
    }

    .ofr-kpi {
      padding: 10px;

      &__icon {
        display: none;
      }

      &__value {
        font-size: 1.05rem;
      }
    }

    .ofr-filters__buttons {
      justify-content: stretch;

      .btn {
        flex: 1 1 0;
      }
    }

    .ofr-pagination {
      justify-content: center;

      &__info,
      &__size {
        width: 100%;
        justify-content: center;
        text-align: center;
      }
    }

    .ofr-col-client {
      min-width: 140px;
    }
  }
}

.ofr-columns-popover {
  min-width: 260px;
  max-width: 320px;

  .popover-body {
    max-height: 60vh;
    overflow-y: auto;
  }

  .form-check {
    padding-top: 2px;
    padding-bottom: 2px;
  }

  .form-check-input:checked {
    accent-color: #0D6247;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e4e6eb;

    a {
      margin-left: auto;
      color: #0D6247;
    }
  }

  &__hidden {
    color: #da640a;
    font-weight: 600;
  }

  &__all {
    margin-left: auto;
    color: #9aa0a6;
  }
}

.ofr-tooltip .tooltip-inner {
  max-width: 320px;
  text-align: left;
}
</style>
